import { z } from 'zod';
import { amountScale, currencyCodeSchema } from './currency.js';
import { fitsCurrencyScale, moneyAmountSchema } from './money.js';
import { OCCURRENCE_STATES, RECURRING_FREQUENCIES } from './installments.js';
import { descriptionSchema, isoDateSchema, payeeNameSchema, positiveAmountSchema, } from './transactions.js';
/**
 * Recurring transactions (Phase 5 PR 1, docs/plans/phase-5.md). A template is a pre-filled
 * expense, income or transfer with a schedule. By default the app never records on its own:
 * each occurrence becomes due and "Record" opens Add pre-filled. With `autoRecord` (fixed amounts
 * only) a daily job records it on its due date as the template's creator. Schedules come from the
 * shared engine in installments.ts.
 */
export const recurringFrequencySchema = z.enum(RECURRING_FREQUENCIES);
export const occurrenceStateSchema = z.enum(OCCURRENCE_STATES);
export const RECURRING_KINDS = ['expense', 'income', 'transfer'];
export const recurringKindSchema = z.enum(RECURRING_KINDS);
const isMoneyString = (v) => typeof v === 'string' && moneyAmountSchema.safeParse(v).success;
/** How far ahead "Due soon" looks (days), as for installments. */
export const DUE_SOON_DAYS = 7;
/** The daily job records missed occurrences at most this many days back; older ones stay due. */
export const AUTO_RECORD_CATCH_UP_DAYS = 31;
/** POST /api/v1/recurring (create) and PUT /api/v1/recurring/:id (change from now on). */
export const recurringTemplateRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(60),
    kind: recurringKindSchema,
    /** Optional when the amount changes every time; required to record automatically. */
    amount: positiveAmountSchema.nullable().default(null),
    currency: currencyCodeSchema,
    /** Expense/income: the money account. Transfer: the "from" account. */
    accountId: z.string().uuid(),
    toAccountId: z.string().uuid().nullable().default(null),
    categoryId: z.string().uuid().nullable().default(null),
    payeeId: z.string().uuid().nullable().default(null),
    payeeName: payeeNameSchema.optional(),
    description: descriptionSchema.default(''),
    autoRecord: z.boolean().default(false),
    frequency: recurringFrequencySchema,
    dayOfMonth: z.number().int().min(1).max(31).nullable().default(null),
    /** 0 = Sunday ... 6 = Saturday. */
    weekday: z.number().int().min(0).max(6).nullable().default(null),
    startOn: isoDateSchema,
    endOn: isoDateSchema.nullable().default(null),
})
    .superRefine((v, ctx) => {
    if (v.frequency === 'monthly' && v.dayOfMonth === null) {
        ctx.addIssue({ code: 'custom', path: ['dayOfMonth'], message: 'Pick a day of the month' });
    }
    if (v.frequency === 'weekly' && v.weekday === null) {
        ctx.addIssue({ code: 'custom', path: ['weekday'], message: 'Pick a day of the week' });
    }
    if (v.endOn !== null && v.endOn < v.startOn) {
        ctx.addIssue({ code: 'custom', path: ['endOn'], message: 'The end is before the start' });
    }
    if (v.kind === 'transfer') {
        if (v.toAccountId === null) {
            ctx.addIssue({ code: 'custom', path: ['toAccountId'], message: 'Pick an account' });
        }
        else if (v.toAccountId === v.accountId) {
            ctx.addIssue({
                code: 'custom',
                path: ['toAccountId'],
                message: 'Pick two different accounts',
            });
        }
    }
    else if (v.categoryId === null) {
        ctx.addIssue({ code: 'custom', path: ['categoryId'], message: 'Pick a category' });
    }
    if (v.autoRecord && v.amount === null) {
        ctx.addIssue({
            code: 'custom',
            path: ['amount'],
            message: 'Recording automatically needs a fixed amount',
        });
    }
    if (v.amount !== null && isMoneyString(v.amount)) {
        const scale = amountScale(v.currency);
        if (!fitsCurrencyScale(v.amount, scale)) {
            ctx.addIssue({
                code: 'custom',
                path: ['amount'],
                message: scale === 0
                    ? `${v.currency} amounts must be whole numbers`
                    : `${v.currency} amounts allow at most ${scale} decimals`,
            });
        }
    }
    if (v.payeeId !== null && v.payeeName !== undefined) {
        ctx.addIssue({
            code: 'custom',
            path: ['payeeName'],
            message: 'Give a payee id or a payee name, not both',
        });
    }
});
/** POST /api/v1/recurring/:id/skip-older: skips every open occurrence due before `dueOn`. */
export const skipOlderRequestSchema = z.object({ dueOn: isoDateSchema });
export const occurrenceSchema = z.object({
    dueOn: z.string(),
    state: occurrenceStateSchema,
    transactionId: z.string().uuid().nullable(),
    auto: z.boolean(),
});
const namedRef = z.object({ id: z.string().uuid(), name: z.string() });
export const recurringTemplateSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    kind: recurringKindSchema,
    amount: moneyAmountSchema.nullable(),
    currency: currencyCodeSchema,
    account: namedRef,
    toAccount: namedRef.nullable(),
    category: namedRef.nullable(),
    payee: namedRef.nullable(),
    description: z.string(),
    autoRecord: z.boolean(),
    frequency: recurringFrequencySchema,
    dayOfMonth: z.number().int().nullable(),
    weekday: z.number().int().nullable(),
    startOn: z.string(),
    endOn: z.string().nullable(),
    paused: z.boolean(),
    /** Past its end date with nothing left to record. */
    ended: z.boolean(),
    /** The next due date from today on (null when paused or ended). */
    nextDueOn: z.string().nullable(),
    /** Open occurrences due up to DUE_SOON_DAYS ahead, oldest first (overdue included). */
    due: z.array(occurrenceSchema),
    createdBy: z.object({ memberId: z.string().uuid(), displayName: z.string() }),
});
export const recurringListResponseSchema = z.object({
    items: z.array(recurringTemplateSchema),
    /** Occurrences the daily job recorded after their due date in the last 7 days. */
    lateRecorded: z.object({ count: z.number().int(), latestAt: z.string().nullable() }),
});
//# sourceMappingURL=recurring.js.map